import React, { useState, useMemo } from "react";
import { Col, Container, Row, ButtonGroup, ListGroup } from "react-bootstrap";
import { TrashIcon, SearchIcon } from "@simplex/sx-icons";
import { AppType } from "@common/types/sharedTypes";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { useTranslation } from "react-i18next";
import ListToolbarFilterDropdown from "./ListToolbarFilterDropdown";

export type SortDirection = "asc" | "desc";
export type SortMode = string;

export interface WithId {
  id: string | number;
}

export interface ListToolbarViewProps<T extends WithId> {
  items: T[];
  getSortValue: (item: T, mode: SortMode) => string | number;
  renderItem: (
    item: T,
    index: number,
    realIndex: number,
    nextProjectName: string,
    visibleItems: T[],
  ) => React.ReactNode;
  renderListHeader?: (headerSort: HeaderSortControls) => React.ReactNode;
  getSearchTerms?: (item: T) => string[]; // NEW: for name and key
  id?: string;
  className?: string;
  headerTitle?: string;
  getType?: (item: T) => AppType | string;
  isInProjectlist?: boolean;
  showDeleteButton?: boolean;
  deleteButtonDisabled?: boolean;
  onDeleteButtonClick?: () => void;
  deleteButtonTitle?: string;
  renderToolbarActions?: () => React.ReactNode;
  hideFilterButtonWhenHeaderProvided?: boolean;
}

export interface HeaderSortControls {
  sortMode: SortMode;
  sortDirection: SortDirection;
  cycleSortBy: (mode: string, initialDirection?: SortDirection) => void;
  getDirectionFor: (mode: string) => SortDirection | null;
}

function ListToolbarView<T extends WithId>({
  items,
  getSortValue,
  renderItem,
  renderListHeader,
  getSearchTerms = (item: any) => {
    // Default: include .name, .key, and .keywords (if array or string)
    let terms: string[] = [];
    if (item.name && typeof item.name === "string") terms.push(item.name);
    if (item.key && typeof item.key === "string") terms.push(item.key);
    if (item.keywords) {
      if (Array.isArray(item.keywords)) {
        terms = terms.concat(item.keywords.filter((k: any) => typeof k === "string"));
      } else if (typeof item.keywords === "string") {
        // Split by comma if it's a string
        terms = terms.concat(item.keywords.split(",").map((k: string) => k.trim()));
      }
    }
    return terms;
  },
  id,
  className,
  headerTitle,
  getType,
  isInProjectlist,
  showDeleteButton = false,
  deleteButtonDisabled = true,
  onDeleteButtonClick,
  deleteButtonTitle,
  renderToolbarActions,
  hideFilterButtonWhenHeaderProvided = true,
}: ListToolbarViewProps<T>) {
  const { t } = useTranslation();
  const searchInputId = "list-toolbar-search-input";
  const defaultSortMode: SortMode = "alphabetical";
  const defaultSortDirection: SortDirection = "asc";
  const [sortMode, setSortMode] = useState<SortMode>(defaultSortMode);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  // Empty set = show every app type. Entries are the types the user has unchecked, so a
  // newly added app type is visible by default without needing its own piece of state.
  const [hiddenAppTypes, setHiddenAppTypes] = useState<ReadonlySet<AppType>>(new Set());

  const toggleAppType = (appType: AppType) => {
    setHiddenAppTypes((prev) => {
      const next = new Set(prev);
      if (next.has(appType)) {
        next.delete(appType);
      } else {
        next.add(appType);
      }
      return next;
    });
  };
  const [showActive, setShowActive] = useState(true); // NEW: Active filter
  const [showInactive, setShowInactive] = useState(true); // NEW: Inactive filter

  const cycleSortBy = (nextMode: string, initialDirection: SortDirection = "asc") => {
    if (sortMode !== nextMode) {
      setSortMode(nextMode);
      setSortDirection(initialDirection);
      return;
    }

    if (sortDirection === initialDirection) {
      setSortDirection(initialDirection === "asc" ? "desc" : "asc");
      return;
    }

    setSortMode(defaultSortMode);
    setSortDirection(defaultSortDirection);
  };

  const getDirectionFor = (mode: string): SortDirection | null => {
    if (sortMode !== mode) return null;
    return sortDirection;
  };

  const types = useMemo(() => {
    return getType ? Array.from(new Set(items.map((item) => String(getType(item))))).sort() : [];
  }, [items, getType]);

  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        // Status filter for project list
        if (isInProjectlist && (item as any).status) {
          if ((item as any).status === "active" && !showActive) return false;
          if ((item as any).status === "inactive" && !showInactive) return false;
        }
        // Per-app-type visibility filters
        if (getType) {
          const type = getType(item);
          const appType = AppType[type as keyof typeof AppType];
          if (hiddenAppTypes.has(appType)) return false;
        }
        return true;
      })
      .filter((item) => {
        // Search filter
        if (!searchTerm) return true;
        return getSearchTerms(item).some((term) => term.toLowerCase().includes(searchTerm));
      });
  }, [items, isInProjectlist, showActive, showInactive, getType, hiddenAppTypes, searchTerm, getSearchTerms]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aVal = getSortValue(a, sortMode);
      const bVal = getSortValue(b, sortMode);

      if (typeof aVal === "string" && typeof bVal === "string") {
        const compared = aVal.localeCompare(bVal);
        return sortDirection === "asc" ? compared : -compared;
      }

      const compared = Number(aVal) - Number(bVal);
      return sortDirection === "asc" ? compared : -compared;
    });
  }, [filteredItems, getSortValue, sortMode, sortDirection]);

  const itemIndexById = useMemo(() => {
    const map = new Map<string | number, number>();
    items.forEach((item, idx) => map.set(item.id, idx));
    return map;
  }, [items]);

  const showFilterDropdown = !(renderListHeader && hideFilterButtonWhenHeaderProvided);
  const headerSortControls: HeaderSortControls = {
    sortMode,
    sortDirection,
    cycleSortBy,
    getDirectionFor,
  };

  const containerClassName = ["d-flex", "flex-column", "h-100", className].filter(Boolean).join(" ");

  return (
    <Container fluid id={id} className={containerClassName}>
      <Row className="header-bg-list justify-content-start align-items-center">
        {headerTitle && (
          <Col className="ps-4">
            <span className="header-title">{headerTitle}</span>
          </Col>
        )}
        <Col xs={12}>
          <ButtonGroup>
            {showFilterDropdown && (
              <ListToolbarFilterDropdown
                sortMode={sortMode}
                setSortMode={setSortMode}
                setSortDirection={setSortDirection}
                getType={getType}
                types={types}
                hiddenAppTypes={hiddenAppTypes}
                toggleAppType={toggleAppType}
                isInProjectlist={isInProjectlist}
                showActive={showActive}
                setShowActive={setShowActive}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                t={t}
              />
            )}
            {/* Search Toggle Icon */}
          </ButtonGroup>
          <ButtonGroup className="float-end">
            {renderToolbarActions && renderToolbarActions()}
            {showDeleteButton && (
              <button
                className="search-toggle-icon secondary-btn"
                style={{ cursor: deleteButtonDisabled ? "not-allowed" : "pointer" }}
                disabled={deleteButtonDisabled}
                onClick={() => {
                  if (!deleteButtonDisabled) {
                    onDeleteButtonClick?.();
                  }
                }}
                title={deleteButtonTitle || t("buttons.delete_multiple")}
              >
                <TrashIcon color="currentColor" size={16} />
              </button>
            )}
            <button
              className="search-toggle-icon secondary-btn"
              title={t("buttons.search")}
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShowSearch((prev) => {
                  const newState = !prev;
                  if (newState) {
                    setTimeout(() => {
                      const el = document.getElementById(searchInputId) as HTMLInputElement | null;
                      el?.focus();
                    }, 0);
                  }
                  return newState;
                });
              }}
            >
              <SearchIcon color="currentColor" size={16} />
            </button>
          </ButtonGroup>
        </Col>
      </Row>
      <hr />

      {/* Search Field */}
      {showSearch && (
        <div id="search-input" className="mb-2 px-2">
          <SXEditField
            id={searchInputId}
            type="text"
            fullWidth
            border
            textAlign="left"
            val={searchTerm}
            placeholder={t("filter_and_search_dropdown.search_placeholder")}
            instantupdate
            onOldUpdateCallback={(newValue) => {
              setSearchTerm(newValue.toLowerCase());
            }}
            onUpdate={(newValue) => {
              setSearchTerm(String(newValue).toLowerCase());
            }}
          />
        </div>
      )}

      {/* Create buttons */}

      {renderListHeader && renderListHeader(headerSortControls)}

      <ListGroup as="ul" className="scroll-list flex-grow-1">
        {sortedItems.map((item, index) => (
          <React.Fragment key={item.id}>
            {renderItem(
              item,
              index,
              itemIndexById.get(item.id) ?? -1,
              sortedItems.length > index + 1 ? (sortedItems[index + 1] as any).name : (sortedItems[0] as any).name,
              sortedItems,
            )}
          </React.Fragment>
        ))}
      </ListGroup>
    </Container>
  );
}

export default ListToolbarView;
